import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Box, Button, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * GP-9: This is an optional step in the Calc Job Dialog Wizard for derived policy types.
 * The user selects the dependencies for certain policy types (MAMP dependencies to QAMP and QAMP and BP dependencies for URA)
 */
export const DepSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep }) => {

  const rsrc = useRsrc();

  const [currJobIndex, setCurrJobIndex] = useState(0);

  const [subtitle, setSubtitle] = useState();

  const [depTypes, setDepTypes] = useState([]);
  const [depCompletion, setDepCompletion] = useState(() =>
    Array(calcChainReq.calcJobReqs.length).fill(false)
  );

  const jobReqsList = calcChainReq.calcJobReqs;
  const liabPolType = calcChainReq.liabPolType;

  const currJobReq = useMemo(() => jobReqsList[currJobIndex], [jobReqsList, currJobIndex]);
  const isRepro = calcChainReq.calcJobReqs[0].src !== 'PER';

  // On init
  useEffect(() => {
    const rowsReq = {
      enumCode: 'DEP_TYPE',
      filterMethod: 'polTypeToDepTypes',
      filterReqParam: { polType: liabPolType.code }
    }
    rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', rowsReq).then((deps) => {
      setDepTypes(deps);

      // If user has been through this step before, we check
      const initialCompletion = jobReqsList.map(jobReq => isDepComplete(jobReq.calcDeps ?? [], deps))
      setDepCompletion(initialCompletion);
    });


  }, []);


  // Let parent know what the next page is
  useEffect(() => {
    const allComplete = depCompletion.every(Boolean);
    if (allComplete) {
      setNextStep('Options');
    } else {
      setNextStep(null);
    }
  }, [depCompletion]);


  // Populate entity grid
  const entityListRef = useRef();
  const clientSideRowData = useCallback((setDataSource) => {
    const reqParam = {
      polType: liabPolType.code,
      period: currJobReq.period.code,
      src: currJobReq.src,
      srcId: currJobReq.srcId
    };

    rsrc.data('gp', 'CALC_DATA_PLUGIN', reqParam).then((calcSrcs) => {
      setDataSource(calcSrcs);
      const gridApi = entityListRef.current?.gridRef.current?.gridApiRef.current;

      gridApi.setFilterModel?.(null);
      gridApi.setSortModel?.(null);
      gridApi.setQuickFilter?.('');

      const calcDeps = currJobReq.calcDeps;
      if (calcDeps) {
        const selectedIds = calcDeps.map(dep => dep.rowId);
        gridApi.forEachNode(node => {
          if (selectedIds.includes(node.data.rowId)) {
            node.setSelected(true);
          }
        });
      } else {
        // As a default, pre-select rows
        gridApi.forEachNode(node => {
          if (node.data.isSelected) {
            node.setSelected(true);
          }
        });
        const selectedRows = gridApi.getSelectedRows();
        const deps = selectedRows.map(dep => ({
          rowId: dep.rowId,
          src: dep.src,
          srcId: dep.srcId,
          type: dep.depType
        }));

        setCalcChainReq(prev => {
          const updated = [...prev.calcJobReqs];
          updated[currJobIndex] = { ...updated[currJobIndex], calcDeps: deps };
          return { ...prev, calcJobReqs: updated };
        });
      }
    })

  }, [currJobIndex]);

  // Column Fields
  const augFieldDefs = (fieldToFieldDef) => {
    const columns = ['name', 'polType', 'periodId', 'status', 'cutoffInstant'];

    Object.keys(fieldToFieldDef).forEach(colName => {
      if (!columns.includes(colName)) {
        delete fieldToFieldDef[colName];
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;

    fieldToFieldDef.src = {
      displayName: 'Source',
      field: 'src',
      precedence: 10000
    }

    fieldToFieldDef.srcId = {
      displayName: 'Source Ref Id',
      field: 'srcId',
      precedence: 10010
    }

    fieldToFieldDef.latestClosed = {
      field: 'latestClosed',
      precedence: 10020
    }
  };

  // On select row
  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    const deps = selectedRows.map(r => ({ rowId: r.rowId, src: r.src, srcId: r.srcId, type: r.depType }));
    setCalcChainReq(prev => {
      const updated = [...prev.calcJobReqs];
      updated[currJobIndex] = { ...updated[currJobIndex], calcDeps: deps };
      return { ...prev, calcJobReqs: updated };
    });
  };

  // Check if all necessary dependencies are selected 
  useEffect(() => {
    const deps = currJobReq.calcDeps;
    if (!deps) return;
    const isComplete = isDepComplete(deps)
    setDepCompletion(prev => {
      const updated = [...prev];
      updated[currJobIndex] = isComplete;
      return updated;
    });
  }, [currJobReq.calcDeps])

  const isDepComplete = (deps, expectedDeps = depTypes) => {
    if (!Array.isArray(deps) || !Array.isArray(expectedDeps)) return false;
    if (deps.length === 0 || expectedDeps.length === 0) return false;

    const selectedTypeKey = deps.map(d => d.type).sort().join('|');
    const expectedTypeKey = expectedDeps.map(d => d.code ?? d).sort().join('|');
    return selectedTypeKey === expectedTypeKey;
  };

  // Set subtitle
  useEffect(() => {
    const calcRef = isRepro ? currJobReq.wbkName : currJobReq.period.code;
    const depNames = depTypes.map(dep => dep.displayName);
    let subtitleBuilder = 'Select ' + (depNames.length === 1
      ? depNames[0].code
      : depNames.slice(0, -1).join(', ') + ', and ' + depNames[depNames.length - 1]);

    subtitleBuilder += ` dependencies for ${calcRef} (${currJobIndex + 1} of ${jobReqsList.length}).`;
    setSubtitle(subtitleBuilder);

  }, [currJobIndex, depTypes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1
        }}>
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {jobReqsList.length > 1 &&
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex - 1)}
                disabled={currJobIndex === 0}
                sx={{ whiteSpace: 'nowrap' }}>
                Previous Calculation
              </Button>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex + 1)}
                disabled={currJobIndex === jobReqsList.length - 1 || !depCompletion[currJobIndex]}
                sx={{ whiteSpace: 'nowrap' }}>
                Next Calculation
              </Button>
            </Box>}
        </Box>
      </Box>


      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <EntityList
          key={`${currJobIndex}`}
          ref={entityListRef}
          entity="WBK"
          clientSideRowData={clientSideRowData}
          augFieldDefs={augFieldDefs}
          events={{ onRowSelected: rowSelected }}
          gridOptions={{
            rowSelection: 'multiple',
          }}
        />
      </Box>
    </Box >
  );
};
